import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

import { EMAIL_VERIFICATION_CODE_LENGTH } from '../../utils/emailVerificationPrompt';

export const DigitsChangeSources = {
    FILL: 'fill',
    EDIT: 'edit',
};

const SLOTS = ['one', 'two', 'three', 'four', 'five', 'six'];
const DIGIT_PATTERN = /^\d$/;
const LAST_INDEX = EMAIL_VERIFICATION_CODE_LENGTH - 1;

const onlyDigits = (value) => (value || '').replace(/\D/g, '');

const digitSx = (invalid) => (theme) => ({
    width: { xs: 40, sm: 48 },
    height: { xs: 44, sm: 48 },
    p: 0,
    border: `1px solid ${invalid ? theme.appColors.red : theme.appColors.coolGrey500}`,
    borderRadius: '4px',
    fontSize: 20,
    fontWeight: 600,
    textAlign: 'center',
    color: theme.appColors.black,
    '&:focus': {
        borderColor: invalid ? theme.appColors.red : theme.appColors.blue,
        outline: 'none',
    },
});

const CodeInput = ({ digits, onDigitsChange, inputRefs, readOnly, invalid, describedBy, 'data-testid': dataTestId }) => {
    const focusBox = (index) => {
        const input = inputRefs.current[index];

        if (input) {
            input.focus();
            input.select();
        }
    };

    const fillFrom = (start, chars) => {
        if (readOnly) {
            return;
        }

        const next = [...digits];

        if (chars.length >= EMAIL_VERIFICATION_CODE_LENGTH) {
            chars.slice(0, EMAIL_VERIFICATION_CODE_LENGTH).split('').forEach((char, index) => {
                next[index] = char;
            });
            onDigitsChange(next, DigitsChangeSources.FILL);
            focusBox(LAST_INDEX);
            return;
        }

        let index = start;
        chars.split('').forEach((char) => {
            if (index <= LAST_INDEX) {
                next[index] = char;
                index += 1;
            }
        });
        onDigitsChange(next, DigitsChangeSources.EDIT);
        focusBox(Math.min(index, LAST_INDEX));
    };

    const handleChange = (index, event) => {
        const typed = onlyDigits(event.target.value);

        if (typed.length === 0) {
            if (digits[index] !== '') {
                const next = [...digits];
                next[index] = '';
                onDigitsChange(next, DigitsChangeSources.EDIT);
            }

            return;
        }

        // One-time-code autofill and some mobile keyboards deliver the whole code through `change`.
        if (typed.length >= EMAIL_VERIFICATION_CODE_LENGTH) {
            fillFrom(0, typed);
            return;
        }

        // Typing into a filled box yields old + new digit; keep the new one.
        const current = digits[index] || '';
        let char = typed[0];

        if (typed.length > 1 && current !== '' && typed.startsWith(current)) {
            char = typed[typed.length - 1];
        }

        fillFrom(index, char);
    };

    const handleKeyDown = (index, event) => {
        if (readOnly) {
            return;
        }

        // Retyping the digit a box already holds fires no `change`, so it is handled as an edit here.
        if (DIGIT_PATTERN.test(event.key) && digits[index] === event.key) {
            event.preventDefault();
            fillFrom(index, event.key);
            return;
        }

        if (event.key === 'Backspace' && digits[index] === '' && index > 0) {
            event.preventDefault();
            const next = [...digits];
            next[index - 1] = '';
            onDigitsChange(next, DigitsChangeSources.EDIT);
            focusBox(index - 1);
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            focusBox(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < LAST_INDEX) {
            event.preventDefault();
            focusBox(index + 1);
        }
    };

    const handlePaste = (index, event) => {
        event.preventDefault();
        const pasted = onlyDigits(event.clipboardData?.getData('text'));

        if (pasted.length === 0) {
            return;
        }

        fillFrom(index, pasted);
    };

    const handleFocus = (event) => {
        event.target.select();
    };

    return (
        <Box
            component="fieldset"
            aria-label="Verification code"
            sx={{ display: 'flex', justifyContent: 'center', gap: { xs: '6px', sm: '8px' }, m: '8px 0 0', p: 0, border: 'none' }}
            data-testid={dataTestId && `${dataTestId}.code-inputs`}
        >
            {SLOTS.map((slot, index) => (
                <Box
                    component="input"
                    key={slot}
                    ref={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${index + 1} of ${EMAIL_VERIFICATION_CODE_LENGTH}`}
                    aria-invalid={invalid}
                    aria-describedby={describedBy}
                    sx={digitSx(invalid)}
                    value={digits[index] || ''}
                    readOnly={readOnly}
                    onChange={(event) => handleChange(index, event)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={(event) => handlePaste(index, event)}
                    onFocus={handleFocus}
                    data-testid={dataTestId && `${dataTestId}.code-input-${index}`}
                />
            ))}
        </Box>
    );
};

CodeInput.propTypes = {
    digits: PropTypes.arrayOf(PropTypes.string).isRequired,
    onDigitsChange: PropTypes.func.isRequired,
    inputRefs: PropTypes.object.isRequired,
    readOnly: PropTypes.bool,
    invalid: PropTypes.bool,
    describedBy: PropTypes.string,
    'data-testid': PropTypes.string,
};

CodeInput.defaultProps = {
    readOnly: false,
    invalid: false,
    describedBy: null,
    'data-testid': null,
};

export default CodeInput;
